import React, { useContext, useRef, useState } from 'react'
import { useMatches } from 'react-router-dom'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation as useFactorialNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

interface LayoutContextInterface {
  isEmbedded: boolean
  fullScreenEnabled: boolean
  isFullScreen: boolean
  toggleFullScreen: () => void
}

const LayoutContext = React.createContext<LayoutContextInterface | null>(null)

const useEmbedded = () => {
  const isEmbedded = Boolean(window.ReactNativeWebView)

  return {
    isEmbedded,
  }
}

const useResponsiveReady = () => {
  const { navigation } = useFactorialNavigation()

  const matches = useMatches()

  const isResponsiveReady =
    // We look for the last responsive route in the matches
    matches
      .map(
        (match) =>
          !!(
            match.handle &&
            typeof match.handle === 'object' &&
            'responsive' in match.handle &&
            !!match.handle.responsive
          )
      )
      .filter((value) => value !== undefined)
      .at(-1) === true

  const pathnameToCheck = matches.at(-1)?.pathname.replace(/\/$/, '') ?? ''

  if (pathnameToCheck === navigation.dashboard.fullPath()) {
    return {
      isResponsiveReady: true,
    }
  }

  return {
    isResponsiveReady,
  }
}

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isFullScreen, setIsFullScreen] = useState(false)

  const { isEmbedded } = useEmbedded()
  const { isResponsiveReady } = useResponsiveReady()

  const previouslyResponsive = useRef(isResponsiveReady)

  useUnsafeEffect(() => {
    const metaTag = document.createElement('meta')
    metaTag.name = 'viewport'
    if (isResponsiveReady || isEmbedded) {
      metaTag.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
    } else {
      metaTag.content =
        'width=device-width, initial-scale=0.5, minimum-scale=0.25, maximum-scale=5.0, user-scalable=yes'
    }
    document.head.appendChild(metaTag)

    if (isEmbedded && !isResponsiveReady && previouslyResponsive.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage('leaving_mobile_ready')
      } else {
        alert(i18n.t('layout.leaving_mobile_ready'))
      }
    }

    previouslyResponsive.current = isResponsiveReady

    return () => {
      document.head.removeChild(metaTag)
    }
  }, [isResponsiveReady, isEmbedded])

  const closeFullScreen = useUnsafeCallback(() => {
    if (!isFullScreen) return

    setIsFullScreen(false)

    track('View full screen cancel')
  }, [isFullScreen, setIsFullScreen])

  const openFullScreen = useUnsafeCallback(() => {
    if (isFullScreen) return

    setIsFullScreen(true)

    track('View full screen success')
  }, [isFullScreen, setIsFullScreen])

  const toggleFullScreen = useUnsafeCallback(() => {
    isFullScreen ? closeFullScreen() : openFullScreen()
  }, [isFullScreen, closeFullScreen, openFullScreen])

  useUnsafeEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeFullScreen()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [closeFullScreen])

  // When changing between focus and default mode we need to recalculate the grid layout in the dashboard
  useUnsafeEffect(() => {
    window.dispatchEvent(new Event('resize'))
  }, [isFullScreen])

  return (
    <LayoutContext.Provider
      value={{
        fullScreenEnabled: true,
        isFullScreen: isFullScreen,
        isEmbedded,
        toggleFullScreen,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export const useLayoutContext = () => {
  const context = useContext(LayoutContext)
  if (!context) throw new Error('Please use this context within a LayoutProvider.')
  return context
}
